/**
 * Supabase client construction and error normalisation.
 *
 * Both apps talk to the database exclusively through the RPCs defined in
 * `supabase/migrations`. Those functions raise `check_violation` with a message
 * written for a customer or a merchant to read, so the job here is to preserve
 * that message rather than replace it with something generic.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

import type { Database } from './database.types';

export type DbClient = SupabaseClient<Database>;

export interface SupabaseConfig {
  url: string;
  anonKey: string;
  /** Distinguishes the two apps' persisted sessions when they share an origin. */
  storageKey?: string;
  /**
   * Whether to read a session out of the page URL on load.
   *
   * Off by default: the customer app arrives from QR links and never carries
   * auth parameters, so parsing every URL for them would be dead work. The
   * merchant app turns it on because an OAuth provider redirects back with the
   * session in the URL fragment, and without this the callback is ignored and
   * the merchant is returned to the sign-in screen with nothing to show for it.
   */
  detectSessionInUrl?: boolean;
}

/**
 * Creates a Supabase client for one app.
 *
 * @param config Project URL, anon key, session storage key, and URL detection.
 * @returns A typed Supabase client.
 */
export function createDbClient(config: SupabaseConfig): DbClient {
  return createClient<Database>(config.url, config.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: config.detectSessionInUrl ?? false,
      // PKCE, matching the server. An SPA cannot keep a client secret, and the
      // implicit flow returns tokens in the URL fragment where they land in
      // browser history and referrers. This is also what the project's auth
      // config is set to, and the two must agree: a callback issued under one
      // flow cannot be completed under the other, which is a session check that
      // rejects rather than returns null — and an app that waits forever.
      flowType: 'pkce',
      storageKey: config.storageKey ?? 'yummy.auth',
    },
    realtime: {
      // The merchant queue can burst when a lunch rush lands; the default of 10
      // messages per second is enough given each order produces a handful of
      // row events.
      params: { eventsPerSecond: 10 },
    },
    global: {
      headers: { 'x-application-name': 'yummy' },
    },
  });
}

/**
 * A failure returned by the database.
 *
 * `message` is taken verbatim from the server, because those strings are the
 * ones the migrations wrote to be shown to a person.
 */
export class ApiError extends Error {
  readonly code: string | undefined;
  readonly details: string | undefined;
  readonly hint: string | undefined;

  /**
   * @param message Server-supplied, user-presentable message.
   * @param code Postgres SQLSTATE or PostgREST error code.
   * @param details Optional server detail string.
   * @param hint Optional server hint string.
   */
  constructor(message: string, code?: string, details?: string, hint?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.hint = hint;
  }

  /** True when the failure is an authorisation problem rather than bad input. */
  get isPermissionError(): boolean {
    return this.code === '42501' || this.code === 'insufficient_privilege';
  }

  /** True when the server rejected the request as invalid input. */
  get isValidationError(): boolean {
    return this.code === '23514' || this.code === 'check_violation';
  }
}

interface PostgrestLikeError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

/**
 * Raises an {@link ApiError} for a failed PostgREST response.
 *
 * @param response The `{ data, error }` pair returned by `supabase-js`.
 * @returns The non-null data.
 * @throws ApiError when `error` is set.
 */
export function unwrap<T>(response: { data: T | null; error: PostgrestLikeError | null }): T {
  if (response.error) {
    throw new ApiError(
      response.error.message,
      response.error.code,
      response.error.details,
      response.error.hint,
    );
  }
  return response.data as T;
}

/**
 * Reads a required Vite environment variable.
 *
 * @param key The variable name, including the `VITE_` prefix.
 * @returns The value.
 * @throws Error naming the missing variable, so misconfiguration fails at boot
 * rather than as an opaque network error later.
 */
export function requireEnv(key: string): string {
  const value = import.meta.env?.[key] as string | undefined;
  if (!value) {
    throw new Error(
      `Missing ${key}. Copy .env.local.example to .env.local at the repository root and fill it in.`,
    );
  }
  return value;
}

/**
 * Builds the client both apps use, reading configuration from the root
 * `.env.local` (shared by both Vite apps through `envDir`).
 *
 * @param storageKey Session storage key, distinct per app.
 * @param options Whether to read a session out of the page URL on load.
 * @returns A configured client.
 */
export function createAppClient(
  storageKey: string,
  options: { detectSessionInUrl?: boolean } = {},
): DbClient {
  return createDbClient({
    url: requireEnv('VITE_SUPABASE_URL'),
    anonKey: requireEnv('VITE_SUPABASE_ANON_KEY'),
    storageKey,
    detectSessionInUrl: options.detectSessionInUrl ?? false,
  });
}
